import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Breadcrumbs,
  Button,
  Avatar,
  Grid,
  Pagination,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import HistoryIcon from "@mui/icons-material/History";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import AppsIcon from "@mui/icons-material/Apps";
import GavelIcon from "@mui/icons-material/Gavel";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import {
  getAgencyById,
  getAgencyAuditLog,
  getSalesStaff,
  assignAgency,
  getAgencyEngagement,
  toggleAgencyMilestone,
  addAgencyNote,
  prepareContract,
} from "../../api/auth";
import DashboardLayout from "../../layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { brand } from "../../theme";
import { toast } from "react-toastify";
import { ROLES } from "../../constants/roles";
import { EmptyCapsule } from "../../components/EmptyState";

const LOG_META = {
  "kyc.submitted": { label: "KYC Submitted", color: "info" },
  "pcc.processed": { label: "PCC Processed", color: "warning" },
  "contract.signed": { label: "Contract Signed", color: "success" },
  "admin.invited": { label: "Admin Invited", color: "secondary" },
};

function statusColor(status) {
  switch (status?.toLowerCase()) {
    case "approved":
    case "signed":
    case "provided":
    case "completed":
      return "success";
    case "pending":
    case "submitted":
      return "warning";
    case "rejected":
    case "not signed":
    case "not submitted":
      return "error";
    default:
      return "default";
  }
}

function InfoField({ label, value }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="#94a3b8" display="block" mb={0.25}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} color="#0f172a">
        {value}
      </Typography>
    </Box>
  );
}

function ReadOnlyField({ label, value, highlight }) {
  if (!value) return null;
  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      size="small"
      InputProps={{ readOnly: true }}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: highlight ? "#f0fdf4" : "#f8fafc",
        },
        "& .MuiOutlinedInput-input": {
          fontWeight: highlight ? 700 : 500,
          color: highlight ? "#16a34a" : "#0f172a",
          fontFamily: highlight ? "monospace" : "inherit",
          WebkitTextFillColor: highlight ? "#16a34a" : "#0f172a",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: highlight ? "#bbf7d0" : "#e2e8f0",
        },
      }}
    />
  );
}

function StageCard({ icon, title, status, details, actions, isEmpty, emptyMessage }) {
  const sc = statusColor(status);

  return (
    <Card sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 2,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor:
              sc === "success"
                ? "#dcfce7"
                : sc === "warning"
                  ? "#fef3c7"
                  : sc === "error"
                    ? "#fee2e2"
                    : "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color:
              sc === "success"
                ? "#16a34a"
                : sc === "warning"
                  ? "#d97706"
                  : sc === "error"
                    ? "#dc2626"
                    : "#94a3b8",
            "& svg": { fontSize: "1.1rem" },
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box flex={1}>
          <Typography variant="body1" fontWeight={600} color="#0f172a">
            {title}
          </Typography>
        </Box>

        <Chip
          label={status || "Not Started"}
          color={sc === "default" ? undefined : sc}
          size="small"
          sx={
            sc === "default"
              ? { bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600 }
              : {}
          }
        />
      </Box>

      <Divider />
      <Box px={3} py={2.5}>
        {isEmpty ? (
          <EmptyCapsule text={emptyMessage} />
        ) : (
          <Grid container spacing={2}>
            {details
              .filter((d) => d.value)
              .map((d, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <ReadOnlyField label={d.label} value={d.value} highlight={d.highlight} />
                </Grid>
              ))}
          </Grid>
        )}

        {actions?.filter(Boolean).length > 0 && (
          <Box
            mt={2.5}
            display="flex"
            gap={1.5}
            flexWrap="wrap"
            justifyContent={isEmpty ? "center" : "flex-start"}
          >
            {actions.filter(Boolean).map((action, i) => (
              <Button
                key={i}
                variant="contained"
                color={action.type || "primary"}
                size="small"
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}

function AssignmentCard({ agency, salesStaff, onSave, canEdit }) {
  const [specialistId, setSpecialistId] = useState(agency?.assignedSpecialist?._id || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ specialistId: specialistId || null });
      toast.success("Assignment saved.");
    } catch {
      toast.error("Failed to save assignment.");
    } finally {
      setSaving(false);
    }
  };

  const isDirty = specialistId !== (agency?.assignedSpecialist?._id || "");

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PeopleAltIcon sx={{ fontSize: "1rem", color: "#64748b" }} />
          <Typography variant="caption" sx={{ color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            Sales Assignment
          </Typography>
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small" disabled={!canEdit}>
              <InputLabel>Sales/Account Specialist</InputLabel>
              <Select value={specialistId} label="Sales/Account Specialist" onChange={(e) => setSpecialistId(e.target.value)}>
                <MenuItem value=""><em>Unassigned</em></MenuItem>
                {salesStaff.specialists.map((s) => {
                  const territory = (s.coveredStates || []).map((state) =>
                    state === "Lagos" && s.coveredLagosZones?.length > 0
                      ? `Lagos (${s.coveredLagosZones.join("/")})`
                      : state
                  );
                  const label = territory.length > 0 ? territory : s.coveredCountries || [];
                  return (
                    <MenuItem key={s._id} value={s._id}>
                      {s.name || s.email}
                      {label.length > 0 && (
                        <Typography component="span" variant="caption" color="#94a3b8" sx={{ ml: 1 }}>
                          ({label.slice(0, 2).join(", ")}{label.length > 2 ? "…" : ""})
                        </Typography>
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {canEdit && (
          <Box mt={2.5} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              disabled={!isDirty || saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save Assignment"}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function EngagementCard({ agencyId, engagement, canEdit, onToggleMilestone, onAddNote }) {
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  const handleToggle = async (key) => {
    setTogglingKey(key);
    try {
      await onToggleMilestone(key);
    } catch {
      toast.error("Failed to update milestone.");
    } finally {
      setTogglingKey(null);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await onAddNote(noteText.trim());
      setNoteText("");
      toast.success("Note added.");
    } catch {
      toast.error("Failed to add note.");
    } finally {
      setAddingNote(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <WorkHistoryIcon sx={{ fontSize: "1rem", color: "#64748b" }} />
          <Typography variant="caption" sx={{ color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            Engagement
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} color="#0f172a" mb={1}>
          Milestones
        </Typography>
        <Box display="flex" flexDirection="column" mb={2.5}>
          {engagement.milestones.map((m) => (
            <Box key={m.key} display="flex" alignItems="center" gap={0.5}>
              <Checkbox
                size="small"
                checked={m.done}
                disabled={!canEdit || togglingKey === m.key}
                onChange={() => handleToggle(m.key)}
              />
              <Box>
                <Typography variant="body2" color="#0f172a">
                  {m.label}
                </Typography>
                {m.done && m.completedAt && (
                  <Typography variant="caption" color="#94a3b8">
                    {m.completedBy?.name || m.completedBy?.email} —{" "}
                    {new Date(m.completedAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant="subtitle2" fontWeight={600} color="#0f172a" mb={1}>
          Notes
        </Typography>
        {canEdit && (
          <Box display="flex" gap={1} mb={2}>
            <TextField
              placeholder="Log a visit, call, or update…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              size="small"
              fullWidth
              multiline
              maxRows={4}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAddNote}
              disabled={!noteText.trim() || addingNote}
              sx={{ flexShrink: 0, alignSelf: "flex-start" }}
            >
              Add
            </Button>
          </Box>
        )}

        {engagement.notes.length === 0 ? (
          <Typography variant="body2" color="#94a3b8">
            No notes logged yet.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {engagement.notes.map((n) => (
              <Box
                key={n._id}
                sx={{ p: 1.5, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #f1f5f9" }}
              >
                <Typography variant="body2" color="#334155">
                  {n.note}
                </Typography>
                <Typography variant="caption" color="#94a3b8">
                  {n.author?.name || n.author?.email} —{" "}
                  {new Date(n.createdAt).toLocaleString("en-GB", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function PrepareContractCard({ currentTerms, preparedAt, canEdit, onSave }) {
  const [terms, setTerms] = useState(currentTerms || "");
  const [saving, setSaving] = useState(false);

  const isDirty = terms.trim() !== (currentTerms || "");

  const handleSave = async () => {
    if (!terms.trim()) return;
    setSaving(true);
    try {
      await onSave(terms.trim());
      toast.success("Contract terms saved. The agency has been notified.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save contract terms.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <GavelIcon sx={{ fontSize: "1rem", color: "#64748b" }} />
          <Typography variant="caption" sx={{ color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            Prepare Contract
          </Typography>
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        <Typography variant="body2" color="#64748b" mb={2}>
          {currentTerms
            ? `Prepared${preparedAt ? ` on ${new Date(preparedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}. Edit below to update the agency's contract terms — they'll be re-notified if you make changes.`
            : "Draft this agency's contract terms below. The agency owner won't be able to sign until you save terms here."}
        </Typography>

        <TextField
          multiline
          minRows={6}
          fullWidth
          placeholder="Type the contract terms for this agency…"
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
          disabled={!canEdit}
          sx={{ mb: 2 }}
        />

        {canEdit && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              disabled={!terms.trim() || !isDirty || saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : currentTerms ? "Update Terms" : "Save & Notify Agency"}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function AgencyDetails() {
  const { id } = useParams();
  const [agency, setAgency] = useState(null);
  const [salesStaff, setSalesStaff] = useState({ specialists: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [engagement, setEngagement] = useState({ milestones: [], notes: [] });
  const { user } = useAuth();
  const navigate = useNavigate();

  const canAssign = user?.role === ROLES.SUPER_ADMIN;
  const canEditEngagement =
    user?.role === ROLES.SUPER_ADMIN ||
    (user?.role === ROLES.SALES_ACCOUNT_SPECIALIST &&
      agency?.assignedSpecialist?._id === user?.id);

  const handleAssign = async (data) => {
    await assignAgency(id, data);
    const res = await getAgencyById(id);
    setAgency(res.data);
  };

  const handleToggleMilestone = async (key) => {
    const res = await toggleAgencyMilestone(id, key);
    setEngagement((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m) => (m.key === key ? res.data : m)),
    }));
  };

  const handleAddNote = async (note) => {
    const res = await addAgencyNote(id, note);
    setEngagement((prev) => ({ ...prev, notes: [res.data, ...prev.notes] }));
  };

  const handlePrepareContract = async (terms) => {
    const res = await prepareContract(id, terms);
    setAgency((prev) => ({
      ...prev,
      contract: { ...prev.contract, ...res.data },
    }));
  };

  useEffect(() => {
    (async () => {
      try {
        const requests = [getAgencyById(id), getAgencyAuditLog(id), getAgencyEngagement(id)];
        if (canAssign) requests.push(getSalesStaff());
        const [agencyRes, auditRes, engagementRes, staffRes] = await Promise.all(requests);
        setAgency(agencyRes.data);
        setAuditLogs(auditRes.data.logs || []);
        setEngagement(engagementRes.data);
        if (staffRes) setSalesStaff(staffRes.data);
      } catch (err) {
        console.error("Failed to load agency:", err);
        setError("Could not load agency details. Please go back and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, canAssign]);

  if (loading)
    return (
      <DashboardLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );

  if (error)
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </DashboardLayout>
    );

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "—";

  const name = agency?.name || "Agency Details";
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const kycSC = statusColor(agency?.kyc?.status);
  const pccSC = statusColor(agency?.pccRequest?.status);

  const steps = [
    {
      icon: <AssignmentIcon />,
      title: "KYC Submission",
      status: agency?.kyc?.status,
      isEmpty: agency?.kyc?.status !== "Submitted",
      emptyMessage: "KYC form not yet submitted",
      details: [
        { label: "Agency Name", value: agency?.kyc?.name },
        { label: "Owner Name", value: agency?.kyc?.ownerName },
        { label: "Address", value: agency?.kyc?.address },
        { label: "Email", value: agency?.kyc?.email },
        { label: "IATA Number", value: agency?.kyc?.iataNumber },
        { label: "NANTA Number", value: agency?.kyc?.nantaNumber },
        { label: "Main PCC", value: agency?.kyc?.mainPcc },
        {
          label: "Branch PCCs",
          value:
            agency?.kyc?.branchPccs?.length > 0
              ? agency.kyc.branchPccs.join(", ")
              : null,
        },
        { label: "IATA Status", value: agency?.kyc?.iataStatus },
        {
          label: "Date Submitted",
          value: formatDate(agency?.kyc?.submittedAt),
        },
      ],
    },
    {
      icon: <FlightTakeoffIcon />,
      title: "PCC Request",
      status: agency?.pccRequest?.status,
      isEmpty:
        !agency?.pccRequest?.status ||
        agency?.pccRequest?.status === "Not Submitted",
      emptyMessage: "PCC not yet requested",
      details: [
        {
          label: "Requested By",
          value: agency?.pccRequest?.requestedBy,
        },
        {
          label: "Request Date",
          value: formatDate(agency?.pccRequest?.requestedAt),
        },
        { label: "PCC Code", value: agency?.pccRequest?.pccCode, highlight: true },
        { label: "EPR", value: agency?.pccRequest?.epr, highlight: true },
        { label: "Processed By", value: agency?.pccRequest?.approvedBy },
        {
          label: "Processed Date",
          value: formatDate(agency?.pccRequest?.processedAt),
        },
        { label: "Notes", value: agency?.pccRequest?.notes },
      ],
      actions:
        user?.role === ROLES.HELPDESK
          ? [
              !agency?.pccRequest?.status ||
              agency?.pccRequest?.status === "Not Submitted"
                ? {
                    label: "Start PCC Request",
                    onClick: () => console.log("Start PCC Request"),
                    type: "primary",
                  }
                : null,
              agency?.pccRequest?.status === "Pending"
                ? {
                    label: "Process PCC Request",
                    onClick: () =>
                      navigate(`/process-pcc/${agency.pccRequest._id}`),
                    type: "success",
                  }
                : null,
            ]
          : [],
    },
    {
      icon: <AppsIcon />,
      title: "Product Request",
      status: agency?.productRequest?.status,
      isEmpty: !agency?.productRequest?.requestedAt,
      emptyMessage: "No product request yet",
      details: [
        {
          label: "Request Date",
          value: formatDate(agency?.productRequest?.requestedAt),
        },
        { label: "Approved By", value: agency?.productRequest?.approvedBy },
      ],
    },
    {
      icon: <GavelIcon />,
      title: "Contract",
      status: agency?.contract?.status,
      isEmpty: agency?.contract?.status !== "Signed",
      emptyMessage: "Contract not yet signed",
      details: [
        { label: "Signed Date", value: formatDate(agency?.contract?.signedAt) },
        { label: "Signatory", value: agency?.contract?.signerName },
      ],
    },
  ];

  return (
    <DashboardLayout>
      <Box>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={
            <NavigateNextIcon fontSize="small" sx={{ color: "#cbd5e1" }} />
          }
          sx={{ mb: 3 }}
        >
          <Link
            to="/dashboard"
            style={{
              textDecoration: "none",
              color: brand.terracotta,
              fontSize: "0.875rem",
            }}
          >
            Dashboard
          </Link>
          <Link
            to="/agencies"
            style={{
              textDecoration: "none",
              color: brand.terracotta,
              fontSize: "0.875rem",
            }}
          >
            Agencies
          </Link>
          <Typography variant="body2" color="#0f172a" fontWeight={500}>
            {name}
          </Typography>
        </Breadcrumbs>

        {/* Hero card */}
        <Card sx={{ mb: 3, overflow: "hidden" }}>
          <Box
            sx={{
              height: 72,
              background: brand.black,
            }}
          />
          <CardContent sx={{ px: 3, pt: 0, pb: 3 }}>
            <Box
              display="flex"
              alignItems="flex-end"
              gap={2.5}
              mt="-36px"
              mb={2.5}
            >
              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  fontSize: "1.4rem",
                  bgcolor: brand.terracotta,
                  border: "3px solid #fff",
                  boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
                  flexShrink: 0,
                }}
              >
                {initials}
              </Avatar>
              <Box pb={0.5}>
                <Typography variant="h5" color="#ffffff">
                  {name}
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mt={0.75}
                  flexWrap="wrap"
                >
                  {agency?.kyc?.status && (
                    <Chip
                      label={`KYC: ${agency.kyc.status}`}
                      color={kycSC === "default" ? undefined : kycSC}
                      size="small"
                      sx={
                        kycSC === "default"
                          ? {
                              bgcolor: "#f1f5f9",
                              color: "#64748b",
                              fontWeight: 600,
                            }
                          : {}
                      }
                    />
                  )}
                  {agency?.pccRequest?.status && (
                    <Chip
                      label={`PCC: ${agency.pccRequest.status}`}
                      color={pccSC === "default" ? undefined : pccSC}
                      size="small"
                      sx={
                        pccSC === "default"
                          ? {
                              bgcolor: "#f1f5f9",
                              color: "#64748b",
                              fontWeight: 600,
                            }
                          : {}
                      }
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField
                  label="Location"
                  value={[agency?.city, agency?.state, agency?.country].filter(Boolean).join(", ")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField label="Email" value={agency?.email} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField label="Phone" value={agency?.phone} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField label="Agency Owner" value={agency?.owner} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InfoField
                  label="Assigned Specialist"
                  value={
                    agency?.assignedSpecialist?.name ||
                    agency?.assignedSpecialist?.email ||
                    "Unassigned"
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Assignment card — visible to super_admin only */}
        {canAssign && (
          <AssignmentCard
            agency={agency}
            salesStaff={salesStaff}
            onSave={handleAssign}
            canEdit={canAssign}
          />
        )}

        {/* Engagement — off-platform activity tracking (milestones + notes) */}
        <EngagementCard
          agencyId={id}
          engagement={engagement}
          canEdit={canEditEngagement}
          onToggleMilestone={handleToggleMilestone}
          onAddNote={handleAddNote}
        />

        {/* Prepare Contract — visible only to the assigned specialist or super_admin */}
        {canEditEngagement && (
          <PrepareContractCard
            currentTerms={agency?.contract?.terms}
            preparedAt={agency?.contract?.preparedAt}
            canEdit={canEditEngagement}
            onSave={handlePrepareContract}
          />
        )}

        {/* Progress steps */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <BusinessIcon sx={{ fontSize: "1rem", color: "#64748b" }} />
            <Typography
              variant="caption"
              sx={{
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 1,
                fontWeight: 600,
              }}
            >
              Agency Progress
            </Typography>
          </Box>

          {steps.map((step, i) => (
            <StageCard key={i} {...step} />
          ))}
        </Box>

        {/* Activity history */}
        {auditLogs.length > 0 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <HistoryIcon sx={{ fontSize: "1rem", color: "#64748b" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontWeight: 600,
                  }}
                >
                  Activity History
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" flexDirection="column" gap={1.5}>
                {auditLogs.slice((auditPage - 1) * 5, auditPage * 5).map((log) => {
                  const meta = LOG_META[log.action] || {
                    label: log.action,
                    color: "default",
                  };
                  return (
                    <Box
                      key={log._id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        p: 1.75,
                        borderRadius: "10px",
                        bgcolor: "#f8fafc",
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      <Box flex={1}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <Chip
                            label={meta.label}
                            size="small"
                            color={
                              meta.color === "default" ? undefined : meta.color
                            }
                            sx={
                              meta.color === "default"
                                ? {
                                    bgcolor: "#e2e8f0",
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }
                                : {}
                            }
                          />
                          {(log.actor?.name || log.actorRole) && (
                            <Typography variant="caption" color="#64748b">
                              by{" "}
                              <span
                                style={{ fontWeight: 600, color: "#475569" }}
                              >
                                {log.actor?.name || log.actorRole}
                              </span>
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="caption"
                        color="#94a3b8"
                        sx={{ whiteSpace: "nowrap", mt: 0.25 }}
                      >
                        {new Date(log.createdAt).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {Math.ceil(auditLogs.length / 5) > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={Math.ceil(auditLogs.length / 5)}
                    page={auditPage}
                    onChange={(_, v) => setAuditPage(v)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </DashboardLayout>
  );
}
